import React, { useState, useEffect, useCallback, useRef } from 'react'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import api from '../services/api'

// ── Types ──────────────────────────────────────────────────────────────────────

interface ColumnMapping {
  [csvColumn: string]: string
}

interface MappingConfidence {
  [csvColumn: string]: 'high' | 'medium' | 'none'
}

interface CustomField {
  id: number
  name: string
  label: string
  field_type: string
  is_required: boolean
}

interface LeadField {
  value: string
  label: string
  required: boolean
  group: 'standard' | 'custom'
}

interface PipelineOption {
  id: number
  name: string
}

interface StageOption {
  id: number
  name: string
  pipeline: number
  display_order: number
}

interface UserOption {
  id: number
  first_name: string
  last_name: string
  email: string
}

interface TeamOption {
  id: number
  name: string
}

interface CSVImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImportComplete: () => void
}

// ── Constants ──────────────────────────────────────────────────────────────────

const STANDARD_FIELDS: LeadField[] = [
  { value: 'name', label: 'Nome', required: true, group: 'standard' },
  { value: 'email', label: 'Email', required: false, group: 'standard' },
  { value: 'phone', label: 'Telefone', required: false, group: 'standard' },
  { value: 'company', label: 'Empresa', required: false, group: 'standard' },
  { value: 'position', label: 'Cargo', required: false, group: 'standard' },
  { value: 'cep', label: 'CEP', required: false, group: 'standard' },
  { value: 'logradouro', label: 'Logradouro', required: false, group: 'standard' },
  { value: 'numero', label: 'Numero', required: false, group: 'standard' },
  { value: 'complemento', label: 'Complemento', required: false, group: 'standard' },
  { value: 'bairro', label: 'Bairro', required: false, group: 'standard' },
  { value: 'cidade', label: 'Cidade', required: false, group: 'standard' },
  { value: 'estado', label: 'Estado', required: false, group: 'standard' },
  { value: 'pais', label: 'Pais', required: false, group: 'standard' },
  { value: 'document_type', label: 'Tipo de Documento', required: false, group: 'standard' },
  { value: 'cpf_cnpj', label: 'CPF/CNPJ', required: false, group: 'standard' },
  { value: 'source', label: 'Origem', required: false, group: 'standard' },
  { value: 'status', label: 'Status', required: false, group: 'standard' },
  { value: 'priority', label: 'Prioridade', required: false, group: 'standard' },
  { value: 'estimated_value', label: 'Valor Estimado', required: false, group: 'standard' },
  { value: 'probability', label: 'Probabilidade', required: false, group: 'standard' },
  { value: 'notes', label: 'Notas', required: false, group: 'standard' },
  { value: 'instagram_handle', label: 'Instagram', required: false, group: 'standard' },
  { value: 'facebook_url', label: 'Facebook', required: false, group: 'standard' },
  { value: 'linkedin_url', label: 'LinkedIn', required: false, group: 'standard' },
  { value: 'compras_count', label: 'Quantidade de Compras', required: false, group: 'standard' },
  { value: 'ticket_medio', label: 'Ticket Medio', required: false, group: 'standard' },
  { value: 'gasto_total', label: 'Gasto Total', required: false, group: 'standard' },
  { value: 'first_contact_date', label: 'Data Primeiro Contato', required: false, group: 'standard' },
  { value: 'follow_up_date', label: 'Data Follow-up', required: false, group: 'standard' },
]

const ALIASES: Record<string, string> = {
  'nome': 'name', 'name': 'name', 'nome completo': 'name',
  'email': 'email', 'e-mail': 'email', 'correio': 'email',
  'telefone': 'phone', 'phone': 'phone', 'celular': 'phone', 'tel': 'phone', 'fone': 'phone',
  'empresa': 'company', 'company': 'company', 'organizacao': 'company',
  'cargo': 'position', 'position': 'position', 'funcao': 'position',
  'cep': 'cep', 'codigo postal': 'cep', 'zip': 'cep',
  'logradouro': 'logradouro', 'endereco': 'logradouro', 'rua': 'logradouro', 'address': 'logradouro',
  'numero': 'numero', 'num': 'numero', 'number': 'numero',
  'complemento': 'complemento',
  'bairro': 'bairro', 'neighborhood': 'bairro',
  'cidade': 'cidade', 'city': 'cidade', 'municipio': 'cidade',
  'estado': 'estado', 'state': 'estado', 'uf': 'estado',
  'pais': 'pais', 'country': 'pais',
  'cpf': 'cpf_cnpj', 'cnpj': 'cpf_cnpj', 'cpf/cnpj': 'cpf_cnpj', 'documento': 'cpf_cnpj',
  'origem': 'source', 'source': 'source', 'canal': 'source',
  'status': 'status',
  'prioridade': 'priority', 'priority': 'priority',
  'valor estimado': 'estimated_value', 'valor': 'estimated_value', 'value': 'estimated_value',
  'probabilidade': 'probability', 'probability': 'probability',
  'notas': 'notes', 'notes': 'notes', 'observacoes': 'notes', 'observacao': 'notes',
  'instagram': 'instagram_handle',
  'facebook': 'facebook_url',
  'linkedin': 'linkedin_url',
  'compras': 'compras_count', 'qtd compras': 'compras_count',
  'ticket medio': 'ticket_medio',
  'gasto total': 'gasto_total',
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function normalizeString(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/[_\-\.]/g, ' ')        // normalize separators
    .replace(/\s+/g, ' ')            // collapse whitespace
}

function autoMapColumns(
  headers: string[],
  allFields: LeadField[]
): { mapping: ColumnMapping; confidence: MappingConfidence } {
  const mapping: ColumnMapping = {}
  const confidence: MappingConfidence = {}
  const usedFields = new Set<string>()

  for (const header of headers) {
    const norm = normalizeString(header)

    // 1. Check aliases (high confidence)
    if (ALIASES[norm] && !usedFields.has(ALIASES[norm])) {
      mapping[header] = ALIASES[norm]
      confidence[header] = 'high'
      usedFields.add(ALIASES[norm])
      continue
    }

    // 2. Check exact match on field value or label
    let bestMatch = ''
    let bestConfidence: 'high' | 'medium' | 'none' = 'none'

    for (const field of allFields) {
      if (usedFields.has(field.value)) continue

      const normValue = normalizeString(field.value)
      const normLabel = normalizeString(field.label)

      if (norm === normValue || norm === normLabel) {
        bestMatch = field.value
        bestConfidence = 'high'
        break
      }

      // Partial match (only upgrade to medium if not already found)
      if (bestConfidence === 'none') {
        if (norm.includes(normValue) || normValue.includes(norm) ||
            norm.includes(normLabel) || normLabel.includes(norm)) {
          if (normValue.length > 2 && normLabel.length > 2) {
            bestMatch = field.value
            bestConfidence = 'medium'
          }
        }
      }
    }

    if (bestMatch) {
      mapping[header] = bestMatch
      confidence[header] = bestConfidence
      usedFields.add(bestMatch)
    } else {
      confidence[header] = 'none'
    }
  }

  return { mapping, confidence }
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const glassCard: React.CSSProperties = {
  background: 'var(--glass-bg-strong)',
  backdropFilter: 'var(--glass-blur)',
  WebkitBackdropFilter: 'var(--glass-blur)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-xl)',
}

const btnPrimary: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--bg-gradient)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-lg)',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 'var(--text-sm)',
}

const btnSecondary: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--glass-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--glass-border-strong)',
  borderRadius: 'var(--radius-lg)',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: 'var(--text-sm)',
  backdropFilter: 'var(--glass-blur)',
}

// ── Component ──────────────────────────────────────────────────────────────────

const CSVImportModal: React.FC<CSVImportModalProps> = ({ isOpen, onClose, onImportComplete }) => {
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing'>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvData, setCsvData] = useState<Record<string, string>[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({})
  const [mappingConfidence, setMappingConfidence] = useState<MappingConfidence>({})
  const [defaultValues, setDefaultValues] = useState<Record<string, string>>({})
  const [updateExisting, setUpdateExisting] = useState(false)
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  const [importResults, setImportResults] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [createDeal, setCreateDeal] = useState(false)
  const [pipelines, setPipelines] = useState<PipelineOption[]>([])
  const [stages, setStages] = useState<StageOption[]>([])
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null)
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null)
  const [assignmentType, setAssignmentType] = useState<'me' | 'user' | 'team'>('me')
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [companyUsers, setCompanyUsers] = useState<UserOption[]>([])
  const [teams, setTeams] = useState<TeamOption[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch custom fields and pipelines on mount
  useEffect(() => {
    if (!isOpen) return
    api.get('/leads/custom-fields/').then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data.results || [])
      setCustomFields(data)
    }).catch(() => {})
    api.get('/pipelines/pipelines/').then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data.results || [])
      setPipelines(data)
    }).catch(() => {})
    api.get('/pipelines/stages/').then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data.results || [])
      setStages(data)
    }).catch(() => {})
    api.get('/auth/users/company_users/').then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data.results || [])
      setCompanyUsers(data)
    }).catch(() => {})
    api.get('/auth/teams/').then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data.results || [])
      setTeams(data)
    }).catch(() => {})
  }, [isOpen])

  // Build combined field list
  const allFields: LeadField[] = [
    ...STANDARD_FIELDS,
    ...customFields.map(cf => ({
      value: `custom:${cf.name}`,
      label: cf.label,
      required: cf.is_required,
      group: 'custom' as const,
    })),
  ]

  // ── File parsing ───────────────────────────────────────────────────────────

  const parseFile = useCallback(async (f: File) => {
    setFile(f)
    setError('')

    const ext = f.name.split('.').pop()?.toLowerCase()

    try {
      if (ext === 'xlsx' || ext === 'xls') {
        // Excel parsing
        const buffer = await f.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' })

        if (jsonData.length === 0) {
          setError('Planilha vazia')
          return
        }

        const headers = Object.keys(jsonData[0])
        setCsvHeaders(headers)
        setTotalRows(jsonData.length)

        // Preview: first 5 rows
        const preview = jsonData.slice(0, 5).map(row => {
          const r: Record<string, string> = {}
          headers.forEach(h => { r[h] = String(row[h] ?? '') })
          return r
        })
        setCsvData(preview)

        // Auto-map
        const { mapping, confidence } = autoMapColumns(headers, allFields)
        setColumnMapping(mapping)
        setMappingConfidence(confidence)
        setStep('mapping')

      } else {
        // CSV parsing with PapaParse
        Papa.parse(f, {
          header: true,
          skipEmptyLines: true,
          encoding: 'UTF-8',
          preview: 0, // parse all for count
          complete: (results) => {
            if (!results.meta.fields || results.meta.fields.length === 0) {
              setError('Arquivo CSV vazio ou sem cabecalhos')
              return
            }

            const headers = results.meta.fields
            setCsvHeaders(headers)
            setTotalRows(results.data.length)

            // Preview: first 5 rows
            const preview = (results.data as Record<string, string>[]).slice(0, 5)
            setCsvData(preview)

            // Auto-map
            const { mapping, confidence } = autoMapColumns(headers, allFields)
            setColumnMapping(mapping)
            setMappingConfidence(confidence)
            setStep('mapping')
          },
          error: (err) => {
            setError(`Erro ao ler CSV: ${err.message}`)
          },
        })
      }
    } catch (err: any) {
      setError(`Erro ao processar arquivo: ${err.message}`)
    }
  }, [allFields])

  // ── Drag & drop ────────────────────────────────────────────────────────────

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) parseFile(f)
  }, [parseFile])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) parseFile(f)
  }

  // ── Template download ──────────────────────────────────────────────────────

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/leads/csv-imports/download-template/', {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'template_importacao_leads.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      // Fallback: generate client-side
      const headers = STANDARD_FIELDS.map(f => f.label)
        .concat(customFields.map(cf => cf.label))
      const csv = '\ufeff' + headers.join(',') + '\n'
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'template_importacao_leads.csv'
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  // ── Import ─────────────────────────────────────────────────────────────────

  const handleProceedToPreview = () => {
    if (!Object.values(columnMapping).includes('name')) {
      setError('Voce deve mapear o campo Nome (obrigatorio)')
      return
    }
    setError('')
    setStep('preview')
  }

  const handleImport = async () => {
    if (!file) return

    setIsLoading(true)
    setError('')

    try {
      const formData = new FormData()

      // If file is Excel, convert to CSV before sending (backend only reads CSV)
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (ext === 'xlsx' || ext === 'xls') {
        const buffer = await file.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const csvString = XLSX.utils.sheet_to_csv(sheet)
        const csvBlob = new Blob([csvString], { type: 'text/csv' })
        formData.append('file', csvBlob, file.name.replace(/\.xlsx?$/i, '.csv'))
      } else {
        formData.append('file', file)
      }

      formData.append('column_mapping', JSON.stringify(columnMapping))
      formData.append('default_values', JSON.stringify(defaultValues))
      formData.append('update_existing', updateExisting.toString())
      formData.append('skip_duplicates', skipDuplicates.toString())
      formData.append('assignment_type', assignmentType)
      if (assignmentType === 'user' && selectedOwnerId) {
        formData.append('owner_id', selectedOwnerId.toString())
      }
      if (assignmentType === 'team' && selectedTeamId) {
        formData.append('team_id', selectedTeamId.toString())
      }
      if (createDeal && selectedPipelineId) {
        formData.append('pipeline_id', selectedPipelineId.toString())
        if (selectedStageId) {
          formData.append('stage_id', selectedStageId.toString())
        }
      }

      // Don't set Content-Type manually — axios auto-detects FormData and adds boundary
      const response = await api.post('/leads/csv-imports/import-csv/', formData)

      setImportResults(response.data)
      setStep('importing')
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Erro ao importar'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  // ── Reset ──────────────────────────────────────────────────────────────────

  const handleClose = () => {
    setStep('upload')
    setFile(null)
    setCsvHeaders([])
    setCsvData([])
    setTotalRows(0)
    setColumnMapping({})
    setMappingConfidence({})
    setDefaultValues({})
    setUpdateExisting(false)
    setSkipDuplicates(true)
    setImportResults(null)
    setError('')
    setCreateDeal(false)
    setSelectedPipelineId(null)
    setSelectedStageId(null)
    setAssignmentType('me')
    setSelectedOwnerId(null)
    setSelectedTeamId(null)
    onClose()
  }

  if (!isOpen) return null

  // Count mapped columns
  const mappedCount = Object.values(columnMapping).filter(Boolean).length
  const unmappedCount = csvHeaders.length - mappedCount
  const hasRequiredName = Object.values(columnMapping).includes('name')

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div
        style={{
          ...glassCard,
          padding: 'var(--space-6)',
          maxWidth: '960px',
          width: '92%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-2xl)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)' }}>
              {step === 'upload' && 'Importar Leads'}
              {step === 'mapping' && 'Mapeamento de Colunas'}
              {step === 'preview' && 'Previa da Importacao'}
              {step === 'importing' && 'Resultado da Importacao'}
            </h2>
            {/* Step indicator */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
              {['Upload', 'Mapeamento', 'Previa', 'Resultado'].map((s, i) => {
                const stepIndex = ['upload', 'mapping', 'preview', 'importing'].indexOf(step)
                const isActive = i === stepIndex
                const isDone = i < stepIndex
                return (
                  <div key={s} style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                    fontSize: 'var(--text-xs)', color: isActive ? 'var(--color-primary-600)' : isDone ? 'var(--color-success-600)' : 'var(--text-tertiary)',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    <span style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 700,
                      background: isActive ? 'var(--bg-gradient)' : isDone ? 'var(--color-success-100)' : 'var(--bg-tertiary)',
                      color: isActive ? 'white' : isDone ? 'var(--color-success-600)' : 'var(--text-tertiary)',
                    }}>
                      {isDone ? '✓' : i + 1}
                    </span>
                    {s}
                    {i < 3 && <span style={{ color: 'var(--text-tertiary)', margin: '0 2px' }}>›</span>}
                  </div>
                )
              })}
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none', border: 'none', fontSize: '20px',
              cursor: 'pointer', padding: 'var(--space-1)', color: 'var(--text-tertiary)',
              borderRadius: 'var(--radius-md)', lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: 'var(--color-danger-600)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
          }}>
            {error}
          </div>
        )}

        {/* ═══════════════ Step 1: Upload ═══════════════ */}
        {step === 'upload' && (
          <div>
            {/* Drag & drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                ...glassCard,
                padding: 'var(--space-10)',
                textAlign: 'center',
                cursor: 'pointer',
                borderStyle: 'dashed',
                borderWidth: '2px',
                borderColor: dragOver ? 'var(--color-primary-500)' : 'var(--glass-border-strong)',
                background: dragOver ? 'rgba(99, 102, 241, 0.08)' : 'var(--glass-bg)',
                transition: 'all 0.2s ease',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '48px', marginBottom: 'var(--space-3)', opacity: 0.7 }}>
                {dragOver ? '📥' : '📄'}
              </div>
              <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 var(--space-2)' }}>
                {dragOver ? 'Solte o arquivo aqui' : 'Arraste um arquivo ou clique para selecionar'}
              </p>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', margin: 0 }}>
                Formatos aceitos: CSV, Excel (.xlsx, .xls)
              </p>
            </div>

            {/* Template download */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3) var(--space-4)',
              ...glassCard,
            }}>
              <div>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Template de Importacao
                </p>
                <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                  Baixe o modelo CSV com todos os campos padrao e personalizados
                </p>
              </div>
              <button onClick={downloadTemplate} style={btnSecondary}>
                Baixar Template
              </button>
            </div>

            {/* Info */}
            <div style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3) var(--space-4)',
              background: 'rgba(99, 102, 241, 0.06)',
              border: '1px solid rgba(99, 102, 241, 0.15)',
              borderRadius: 'var(--radius-lg)',
              fontSize: 'var(--text-xs)', color: 'var(--text-secondary)',
            }}>
              O sistema detecta automaticamente as colunas e sugere o mapeamento.
              Voce pode ajustar manualmente antes de importar.
            </div>
          </div>
        )}

        {/* ═══════════════ Step 2: Mapping ═══════════════ */}
        {step === 'mapping' && (
          <div>
            {/* Stats bar */}
            <div style={{
              display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)',
              flexWrap: 'wrap',
            }}>
              <span style={{
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-xs)', fontWeight: 600,
                background: 'rgba(16, 185, 129, 0.1)',
                color: 'var(--color-success-600)',
              }}>
                {mappedCount} mapeadas
              </span>
              {unmappedCount > 0 && (
                <span style={{
                  padding: 'var(--space-1) var(--space-3)',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--text-xs)', fontWeight: 600,
                  background: 'rgba(156, 163, 175, 0.15)',
                  color: 'var(--text-tertiary)',
                }}>
                  {unmappedCount} sem mapeamento
                </span>
              )}
              <span style={{
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-xs)', fontWeight: 600,
                background: hasRequiredName ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: hasRequiredName ? 'var(--color-success-600)' : 'var(--color-danger-600)',
              }}>
                Nome: {hasRequiredName ? '✓' : 'nao mapeado'}
              </span>
              <span style={{
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-xs)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-secondary)',
              }}>
                {totalRows} linhas no arquivo
              </span>
            </div>

            {/* Mapping rows */}
            <div style={{
              ...glassCard,
              overflow: 'hidden',
              marginBottom: 'var(--space-4)',
            }}>
              {/* Table header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '12px 1fr 2fr 1fr',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--bg-tertiary)',
                fontSize: 'var(--text-xs)',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.05em',
              }}>
                <div></div>
                <div>Coluna CSV</div>
                <div>Campo do Lead</div>
                <div>Exemplo</div>
              </div>

              {csvHeaders.map((header, idx) => {
                const conf = mappingConfidence[header] || 'none'
                const confColor = conf === 'high' ? 'var(--color-success-500)' : conf === 'medium' ? 'var(--color-warning-500)' : 'var(--border-light)'
                const sampleValues = csvData
                  .map(row => row[header])
                  .filter(Boolean)
                  .slice(0, 2)

                return (
                  <div
                    key={header}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '12px 1fr 2fr 1fr',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-2) var(--space-4)',
                      alignItems: 'center',
                      borderTop: idx > 0 ? '1px solid var(--border-light)' : 'none',
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    {/* Confidence dot */}
                    <div style={{
                      width: '10px', height: '10px', borderRadius: '50%',
                      background: confColor,
                      flexShrink: 0,
                    }} title={conf === 'high' ? 'Match exato' : conf === 'medium' ? 'Match parcial' : 'Sem match'} />

                    {/* CSV column name */}
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {header}
                    </div>

                    {/* Dropdown */}
                    <select
                      value={columnMapping[header] || ''}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, [header]: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: 'var(--space-2)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--text-sm)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <option value="">-- Nao mapear --</option>
                      <optgroup label="Campos Padrao">
                        {STANDARD_FIELDS.map(f => (
                          <option key={f.value} value={f.value}>
                            {f.label}{f.required ? ' *' : ''}
                          </option>
                        ))}
                      </optgroup>
                      {customFields.length > 0 && (
                        <optgroup label="Campos Personalizados">
                          {customFields.map(cf => (
                            <option key={cf.name} value={`custom:${cf.name}`}>
                              {cf.label}{cf.is_required ? ' *' : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>

                    {/* Sample values */}
                    <div style={{
                      fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {sampleValues.length > 0 ? sampleValues.join(', ') : '—'}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Default values */}
            <div style={{
              ...glassCard,
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}>
              <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text-primary)' }}>
                Valores Padrao
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Origem</label>
                  <select
                    value={defaultValues.source || ''}
                    onChange={(e) => setDefaultValues(prev => ({ ...prev, source: e.target.value }))}
                    style={{
                      width: '100%', padding: 'var(--space-2)',
                      border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">-- Selecionar --</option>
                    <option value="website">Website</option>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="referral">Indicacao</option>
                    <option value="event">Evento</option>
                    <option value="email">Email</option>
                    <option value="phone">Telefone</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Status</label>
                  <select
                    value={defaultValues.status || ''}
                    onChange={(e) => setDefaultValues(prev => ({ ...prev, status: e.target.value }))}
                    style={{
                      width: '100%', padding: 'var(--space-2)',
                      border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">-- Selecionar --</option>
                    <option value="new">Novo</option>
                    <option value="contacted">Contatado</option>
                    <option value="qualified">Qualificado</option>
                    <option value="proposal">Proposta Enviada</option>
                    <option value="negotiation">Negociacao</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Prioridade</label>
                  <select
                    value={defaultValues.priority || ''}
                    onChange={(e) => setDefaultValues(prev => ({ ...prev, priority: e.target.value }))}
                    style={{
                      width: '100%', padding: 'var(--space-2)',
                      border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">-- Selecionar --</option>
                    <option value="low">Baixa</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div style={{ ...glassCard, padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
              <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text-primary)' }}>
                Responsavel pelos Leads
              </h4>
              <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
                {(['me', 'user', 'team'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => { setAssignmentType(type); setSelectedOwnerId(null); setSelectedTeamId(null) }}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: assignmentType === type ? 'var(--primary-500)' : 'var(--border-light)',
                      background: assignmentType === type ? 'rgba(99,102,241,0.1)' : 'var(--bg-primary)',
                      color: assignmentType === type ? 'var(--primary-600)' : 'var(--text-secondary)',
                    }}
                  >
                    {type === 'me' ? 'Eu mesmo' : type === 'user' ? 'Usuário específico' : 'Distribuir para equipe'}
                  </button>
                ))}
              </div>
              {assignmentType === 'user' && (
                <select
                  value={selectedOwnerId ?? ''}
                  onChange={(e) => setSelectedOwnerId(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    width: '100%', padding: 'var(--space-2)',
                    border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                  }}
                >
                  <option value="">-- Selecionar usuário --</option>
                  {companyUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </option>
                  ))}
                </select>
              )}
              {assignmentType === 'team' && (
                <select
                  value={selectedTeamId ?? ''}
                  onChange={(e) => setSelectedTeamId(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    width: '100%', padding: 'var(--space-2)',
                    border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                  }}
                >
                  <option value="">-- Selecionar equipe --</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
                Atualizar leads existentes (se encontrado por email/telefone)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} />
                Pular duplicatas
              </label>
            </div>
          </div>
        )}

        {/* ═══════════════ Step 3: Preview ═══════════════ */}
        {step === 'preview' && (
          <div>
            {/* Summary chips */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
              <span style={{ padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)', fontWeight: 600, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                {totalRows} linhas totais
              </span>
              <span style={{ padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)', fontWeight: 600, background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success-600)' }}>
                {mappedCount} colunas mapeadas
              </span>
              {unmappedCount > 0 && (
                <span style={{ padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)', fontWeight: 600, background: 'rgba(245, 158, 11, 0.1)', color: 'var(--color-warning-600)' }}>
                  {unmappedCount} ignoradas
                </span>
              )}
              <span style={{ padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)', fontWeight: 600, background: updateExisting ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-tertiary)', color: updateExisting ? 'var(--color-primary-600)' : 'var(--text-tertiary)' }}>
                Atualizar existentes: {updateExisting ? 'Sim' : 'Nao'}
              </span>
            </div>

            {/* Preview table */}
            <div style={{ overflowX: 'auto', marginBottom: 'var(--space-4)', ...glassCard }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)' }}>
                <thead>
                  <tr>
                    <th style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left', background: 'var(--bg-tertiary)', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>#</th>
                    {csvHeaders
                      .filter(h => columnMapping[h])
                      .map(header => (
                        <th key={header} style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left', background: 'var(--bg-tertiary)', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {allFields.find(f => f.value === columnMapping[header])?.label || header}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ padding: 'var(--space-2) var(--space-3)', borderTop: '1px solid var(--border-light)', color: 'var(--text-tertiary)' }}>{index + 1}</td>
                      {csvHeaders
                        .filter(h => columnMapping[h])
                        .map(header => (
                          <td key={header} style={{ padding: 'var(--space-2) var(--space-3)', borderTop: '1px solid var(--border-light)', color: 'var(--text-primary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row[header] || '—'}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {csvData.length < totalRows && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', textAlign: 'center', margin: '0 0 var(--space-4)' }}>
                Mostrando {csvData.length} de {totalRows} linhas
              </p>
            )}

            {/* Pipeline / Kanban selection */}
            <div style={{
              ...glassCard,
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                fontSize: 'var(--text-sm)', color: 'var(--text-primary)', cursor: 'pointer',
                fontWeight: 600,
              }}>
                <input
                  type="checkbox"
                  checked={createDeal}
                  onChange={(e) => {
                    setCreateDeal(e.target.checked)
                    if (!e.target.checked) {
                      setSelectedPipelineId(null)
                      setSelectedStageId(null)
                    }
                  }}
                />
                Criar oportunidade no Kanban
              </label>
              <p style={{ margin: 'var(--space-1) 0 0 24px', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                {createDeal
                  ? 'Um deal sera criado para cada lead importado no pipeline selecionado'
                  : 'Nenhum — leads serao importados sem criar oportunidades'}
              </p>

              {createDeal && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginTop: 'var(--space-3)', marginLeft: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Pipeline</label>
                    <select
                      value={selectedPipelineId ?? ''}
                      onChange={(e) => {
                        const id = e.target.value ? Number(e.target.value) : null
                        setSelectedPipelineId(id)
                        setSelectedStageId(null)
                      }}
                      style={{
                        width: '100%', padding: 'var(--space-2)',
                        border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                      }}
                    >
                      <option value="">-- Selecionar Pipeline --</option>
                      {pipelines.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Etapa do Kanban</label>
                    <select
                      value={selectedStageId ?? ''}
                      onChange={(e) => setSelectedStageId(e.target.value ? Number(e.target.value) : null)}
                      disabled={!selectedPipelineId}
                      style={{
                        width: '100%', padding: 'var(--space-2)',
                        border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--text-sm)', background: 'var(--bg-primary)', color: 'var(--text-primary)',
                        opacity: selectedPipelineId ? 1 : 0.5,
                      }}
                    >
                      <option value="">-- Primeira etapa --</option>
                      {stages
                        .filter(s => s.pipeline === selectedPipelineId)
                        .sort((a, b) => a.display_order - b.display_order)
                        .map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════ Step 4: Results ═══════════════ */}
        {step === 'importing' && importResults && (
          <div>
            {/* Status banner */}
            <div style={{
              ...glassCard,
              padding: 'var(--space-6)',
              textAlign: 'center',
              marginBottom: 'var(--space-4)',
              background: importResults.leads_with_error === 0
                ? 'rgba(16, 185, 129, 0.08)'
                : 'rgba(245, 158, 11, 0.08)',
              borderColor: importResults.leads_with_error === 0
                ? 'rgba(16, 185, 129, 0.2)'
                : 'rgba(245, 158, 11, 0.2)',
            }}>
              <div style={{ fontSize: '48px', marginBottom: 'var(--space-3)' }}>
                {importResults.leads_with_error === 0 ? '✅' : '⚠️'}
              </div>
              <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--text-primary)' }}>
                Importacao {importResults.leads_with_error === 0 ? 'concluida com sucesso' : 'concluida com avisos'}
              </h3>
            </div>

            {/* KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
              {[
                { label: 'Total de Linhas', value: importResults.total_rows, color: 'var(--text-secondary)', bg: 'var(--bg-tertiary)' },
                { label: 'Leads Criados', value: importResults.leads_created, color: 'var(--color-success-600)', bg: 'rgba(16, 185, 129, 0.08)' },
                { label: 'Leads Atualizados', value: importResults.leads_updated, color: 'var(--color-primary-600)', bg: 'rgba(99, 102, 241, 0.08)' },
                ...(importResults.deals_created > 0 ? [{ label: 'Oportunidades Criadas', value: importResults.deals_created, color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.08)' }] : []),
                { label: 'Com Erro', value: importResults.leads_with_error, color: 'var(--color-danger-600)', bg: 'rgba(239, 68, 68, 0.08)' },
              ].map(kpi => (
                <div key={kpi.label} style={{ ...glassCard, padding: 'var(--space-4)', background: kpi.bg }}>
                  <div style={{ fontSize: 'var(--text-xs)', color: kpi.color, marginBottom: 'var(--space-1)' }}>{kpi.label}</div>
                  <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                </div>
              ))}
            </div>

            {/* Error details */}
            {importResults.error_details && (
              <div style={{
                ...glassCard,
                padding: 'var(--space-4)',
                background: 'rgba(239, 68, 68, 0.05)',
                borderColor: 'rgba(239, 68, 68, 0.15)',
              }}>
                <h4 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-danger-600)' }}>
                  Detalhes dos Erros
                </h4>
                <pre style={{
                  margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)',
                  whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto',
                }}>
                  {importResults.error_details}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════ Buttons ═══════════════ */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)',
          marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border-light)',
        }}>
          {step === 'upload' && (
            <button onClick={handleClose} style={btnSecondary}>Cancelar</button>
          )}
          {step === 'mapping' && (
            <>
              <button onClick={() => { setStep('upload'); setFile(null); setCsvHeaders([]); setCsvData([]) }} style={btnSecondary}>Voltar</button>
              <button onClick={handleProceedToPreview} style={btnPrimary}>Proximo</button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button onClick={() => setStep('mapping')} style={btnSecondary}>Voltar</button>
              <button
                onClick={handleImport}
                disabled={isLoading}
                style={{
                  ...btnPrimary,
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? 'Importando...' : `Importar ${totalRows} leads`}
              </button>
            </>
          )}
          {step === 'importing' && (
            <button
              onClick={() => { handleClose(); onImportComplete() }}
              style={btnPrimary}
            >
              Concluir
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  )
}

export default CSVImportModal
